import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import {
  FaChevronDown,
  FaChevronUp, 
  FaSearch,
  FaShoppingCart,
} from "react-icons/fa";
import PageContainer from "../../components/PageContainer/PageContainer";
import StorageSection from "../../components/StorageSection/StorageSection";
import RemitoDispatch from "../../components/RemitoDispatch/RemitoDispatch";
import request from "../../utils/request";
import SpinnerQ from "../../components/SpinnerQ/SpinnerQ";
import styles from "./PackageDispatch.module.css";
import t from "../../utils/translation";
import { getSession } from "../../utils/sessionHandler";
import Swal from "sweetalert2";
import BarcodeReader from "react-barcode-reader";
import PackageDetail from "../../components/PackageDetail/PackageDetail";
import LocalizationContext from "../../utils/LocalizationContext";

export default function PackageDispatch({ location }) {
  const [storages, setStorages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStorage, setSelectedStorage] = useState("");
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [destinationUnit, setDestinationUnit] = useState("");
  const [shippingResponsible, setShippingResponsible] = useState("");
  const [dispatchType, setDispatchType] = useState("false");
  const [observations, setObservations] = useState("");
  const [isDispatching, setIsDispatching] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(
    { packageId: location?.state?.packageId } ?? null
  );
  const [generatedDispatch, setGeneratedDispatch] = useState(null);
  const [openStorages, setOpenStorages] = useState(true);

  const locale = useContext(LocalizationContext);

  useEffect(() => {
    fetchStorages();
    fetchAreas();
  }, []);

  const fetchStorages = async () => {
    try {
      setLoading(true);
      const res = await request().get("/api/package/stored");

      const sortedData = res.data.sort((a, b) => {
        if (a.id === null && b.id === null) return 0;
        if (a.id === null) return -1;
        if (b.id === null) return 1;
        return a.id - b.id;
      });

      setStorages(sortedData);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error fetching storages");
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await request().get(
        `/api/area?companyId=${getSession().Company.Id}`
      );
      setAreas(res.data);
    } catch (error) {
      toast.error("Error loading destination areas.");
    }
  };

  const filteredStorages = storages
    .filter((st) => {
      if (!selectedStorage) return true;
      if (selectedStorage === "null") return st.id === null;
      return st.id === parseInt(selectedStorage, 10);
    })
    .map((st) => ({
      ...st,
      packages: st.packages.filter(
        (pkg) =>
          pkg.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(pkg.packageNumber).includes(searchTerm)
      ),
    }))
    .filter((st) => st.packages.length > 0);

  const toggleSelectPackage = (pkg) => {
    setSelectedPackages((prev) =>
      prev.find((p) => p.packageId === pkg.packageId)
        ? prev.filter((p) => p.packageId !== pkg.packageId)
        : [...prev, pkg]
    );
  };
  const handleOpenAllStorages = () => {
    setOpenStorages((prev) => !prev);
  };

  const showDispatchSummary = async (askConfirmation = false) => {
    const areaName = areas.find((a) => a.id == destinationUnit)?.name || "";
    const dispatchTypeText =
      dispatchType === "true" ? t("BlankEntry176") : t("BlankEntry175");

    const packageListHtml =
      selectedPackages.length > 0
        ? `<div style="
          background: #f9f9f9;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 10px;
          margin-top: 6px;
          max-height: 150px;
          overflow-y: auto;
          text-align: left;
        ">
          <ul style="margin: 0; padding-left: 20px; font-size: 0.9rem;">
            ${selectedPackages
              .map(
                (p) =>
                  `<li style="margin-bottom: 4px;">#${p.packageNumber} - ${p.packageName}</li>`
              )
              .join("")}
          </ul>
        </div>`
        : `<p style='margin-top: 6px;'>${t("BlankEntry180")}.</p>`;

    const summaryHtml = `
      <div style="text-align: left; font-size: 0.95rem; line-height: 1.5;">
        <p><strong>📍 ${t("BlankEntry246")}:</strong> ${areaName}</p>
        <p><strong>👤 ${t("BlankEntry173")}:</strong> ${shippingResponsible}</p>
        <p><strong> ${t("BlankEntry174")}:</strong> ${dispatchTypeText}</p>
        ${
          observations
            ? `<p><strong>${t("ObservationSpan")}:</strong> ${observations}</p>`
            : ""
        }
        <p><strong>${t("PackagesSpan")} (${
      selectedPackages.length
    }):</strong></p>
        ${packageListHtml}
      </div>
    `;

    const result = await Swal.fire({
      title: askConfirmation ? t("BlankEntry169") : t("BlankEntry243"),
      html: summaryHtml,
      icon: "info",
      showCancelButton: askConfirmation,
      confirmButtonText: askConfirmation ? t("BlankEntry185") : "OK",
      cancelButtonText: t("CancelButton"),
      focusConfirm: true,
      width: 600,
      background: "var(--white)",
    });

    return result;
  };

  const validation = () => {
    let hasErrors = false;

    if (!destinationUnit) {
      toast.info(t("BlankEntry186"));
      hasErrors = true;
    }

    if (!shippingResponsible) {
      toast.info(t("BlankEntry187"));
      hasErrors = true;
    }

    if (selectedPackages.length === 0) {
      toast.info(t("BlankEntry188"));
      hasErrors = true;
    }

    if (dispatchType === "") {
      toast.info(t("BlankEntry189"));
      hasErrors = true;
    }

    return hasErrors;
  };
  const handleConfirmDispatch = async () => {
    if (validation()) return;
    const result = await showDispatchSummary(true);

    if (!result.isConfirmed) {
      return;
    }

    setIsDispatching(true);

    try {
      const response = await request().post("/api/dispatch", {
        areaId: destinationUnit,
        shippingResponsible: shippingResponsible,
        isUrgent: dispatchType === "true",
        packageNumbers: selectedPackages.map((p) => p.packageNumber),
        observations: observations,
      });

      toast.success(t("BlankEntry190"));
      setSelectedPackages([]);
      setDestinationUnit("");
      setShippingResponsible("");
      setDispatchType("false");
      setObservations("");
      fetchStorages();

      const dispatchData = await request().get(
        `/api/dispatch/list?dispatchId=${response.data}`
      );

      Swal.fire({
        title: t("BlankEntry191"),
        text: t("BlankEntry192"),
        icon: "question",
        input: "number",
        inputLabel: t("BlankEntry193"),
        inputValue: 1,
        inputAttributes: {
          min: 1,
          step: 1,
        },
        showCancelButton: true,
        confirmButtonText: t("BlankEntry194"),
        cancelButtonText: t("CancelButton"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          const copies = parseInt(result.value, 10) || 1;
          setGeneratedDispatch({
            ...dispatchData.data.dispatches[0],
            copiesCount: copies,
          });
        }
      });
    } catch (error) {
      toast.error("Error processing the dispatch request.");
    } finally {
      setIsDispatching(false);
    }
  };

  const onHandleScan = (data) => {
    const infoUser = getSession();
    const idDistributorCompany = infoUser.IdDistributorCompany;

    const cleanId = idDistributorCompany.replace(/-/g, "");
    const prefix = `P${cleanId}`;

    if (data.startsWith(prefix)) {
      const scannedPackageNumber = data.slice(prefix.length);
      console.log(scannedPackageNumber);

      const allPackages = storages.flatMap((st) => st.packages);
      const pkg = allPackages.find(
        (p) => p.packageNumber === parseInt(scannedPackageNumber)
      );

      if (!pkg) {
        toast.error(t("BlankEntry195"));
        return;
      }

      const alreadySelected = selectedPackages.some(
        (sp) => sp.packageId === pkg.packageId
      );

      if (alreadySelected) {
        setSelectedPackages((prev) =>
          prev.filter((item) => item.packageId !== pkg.packageId)
        );
        toast.info(
          `${t("BlankEntry137")} ${pkg.packageNumber} ${t("BlankEntry135")}.`
        );
      } else {
        setSelectedPackages((prev) => [...prev, pkg]);
        toast.success(
          `${t("BlankEntry137")} ${pkg.packageNumber} ${t("BlankEntry136")}.`
        );
      }
    }
  };

  return (
    <PageContainer
      categoryId={4}
      category={t("BlankEntry166") || "Dispatch"}
      backUrl="/appdispatchmenu"
      title={t("BlankEntry167") || "Package Dispatch"}
      subtitle={selectedPackage?.packageId ? t("DetailSpan") : null}
      clickBack={selectedPackage?.packageId ? true : null}
      onClickBack={() => setSelectedPackage({})}
    >
      {loading ? (
        <div className="spinnerContainer">
          <SpinnerQ category="dispatch" />
        </div>
      ) : !selectedPackage?.packageId ? (
        <div className={styles.containerLayout}>
          <BarcodeReader onScan={onHandleScan} />

          {/* Left Column */}
          <div className={styles.leftColumn}>
            <div className={styles.dispatchForm}>
              <label>
                {t("BlankEntry172") || "Select destination area"}
                <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="area"
                value={destinationUnit}
                onChange={(e) => setDestinationUnit(e.target.value)}
              >
                <option value="">
                  {t("BlankEntry172") || "Select destination area"}
                </option>
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>

              <label>
                {t("BlankEntry173") || "Shipping Responsible"}
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                placeholder={t("BlankEntry173") || "Shipping Responsible"}
                value={shippingResponsible}
                onChange={(e) => setShippingResponsible(e.target.value)}
              />

              <label>
                {t("BlankEntry174") || "Dispatch Type"}{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <select
                value={dispatchType}
                onChange={(e) => setDispatchType(e.target.value)}
              >
                <option value="false">
                  {t("BlankEntry175") || "Scheduled"}
                </option>
                <option value="true">{t("BlankEntry176") || "Urgent"}</option>
              </select>

              <div className={styles.observations}>
                <label>{t("ObservationSpan") || "Observations"}</label>
                <textarea
                  placeholder={t("ObservationSpan") || "Observations"}
                  value={observations}
                  maxLength={300}
                  onChange={(e) => setObservations(e.target.value)}
                />
              </div>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmDispatch}
                disabled={isDispatching}
              >
                {isDispatching
                  ? `${t("BlankEntry177") || "Dispatching"}...`
                  : t("BlankEntry178") || "Confirm Dispatch"}
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div className={styles.mainContent}>
            <div className={styles.topBar}>
              <div className={styles.searchWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder={`${t("BlankEntry38") || "Search package..."}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <select
                className={styles.storageFilter}
                value={selectedStorage}
                onChange={(e) => setSelectedStorage(e.target.value)}
              >
                <option value="">{t("BlankEntry179") || "All Storages"}</option>
                {storages
                  .filter((st) => st.packages.length > 0)
                  .map((st) => (
                    <option
                      key={st.id === null ? "null" : st.id}
                      value={st.id === null ? "null" : st.id}
                    >
                      {st.locationName || t("Unassigned") || "Unassigned"}
                    </option>
                  ))}
              </select>
              <button
                className={styles.moveButton}
                onClick={handleOpenAllStorages}
              >
                {openStorages ? (
                  <>
                    <FaChevronUp style={{ marginRight: 6 }} />
                    {t("BlankEntry181")}
                  </>
                ) : (
                  <>
                    <FaChevronDown style={{ marginRight: 6 }} />
                    {t("BlankEntry182")}
                  </>
                )}
              </button>
              <div className={styles.cartWrapper}>
                <div
                  className={styles.cartIconContainer}
                  title={
                    selectedPackages.length > 0
                      ? selectedPackages
                          .map((p) => `#${p.packageNumber} - ${p.packageName}`)
                          .join("\n")
                      : t("BlankEntry180") || "No packages selected"
                  }
                  onClick={() => showDispatchSummary(false)}
                >
                  <FaShoppingCart className={styles.cartIcon} />
                  {selectedPackages.length > 0 && (
                    <div className={styles.cartBadge}>
                      {selectedPackages.length}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {filteredStorages.map((storage) => (
              <StorageSection
                key={storage.id === null ? "nullStorage" : storage.id}
                storageId={storage.id}
                storageName={storage.locationName || t("Unassigned")}
                pkgs={storage.packages}
                selectedPackageIds={selectedPackages.map((p) => p.packageId)}
                handlePackageClick={(id) => {
                  const pkg = storage.packages.find((p) => p.packageId === id);
                  if (pkg) toggleSelectPackage(pkg);
                }}
                toggle={openStorages}
                storageColor={storage.colorValue}
                setSelectedPackage={setSelectedPackage}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.detailContainer}>
          <PackageDetail
            packageId={selectedPackage.packageId}
            locale={locale}
            dispatch={true}
          />
        </div>
      )}
      {generatedDispatch && (
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          <RemitoDispatch dispatchData={generatedDispatch} />
        </div>
      )}
    </PageContainer>
  );
}
